import type { StateCreator } from 'zustand';
import { leadGenApi, type DomainLead, type FunnelStatsHeader, type LeadGenFilters,
         type ScrapePreview, type ScrapePreviewParams, type IcpBand,
         type FunnelPlan, type ReverseFunnelPlan, type ZielModus,
         type BulkFindContactsResult } from '../services/leadGenApi';
import type { FilterBedingung, LeadFilter } from '../services/leadFilterApi';

// Single in-flight scrape-preview request; a newer call aborts the older one (no out-of-order writes).
let _previewAbort: AbortController | null = null;
// Dasselbe fuer den Funnel-Plan: das Panel laedt beim Oeffnen und nach jedem beendeten Lauf.
let _funnelAbort: AbortController | null = null;
// Dasselbe fuer den Rueckwaerts-Rechner: verhindert, dass schnelles Tippen im Zielfeld mehrere
// Anfragen ueberlappen laesst.
let _reverseFunnelAbort: AbortController | null = null;

export interface LeadGenSlice {
  leadGenLeads: DomainLead[];
  leadGenTotal: number;
  // Klassik Stufe 2 (E4): der Server hat den Ausnahmepfad für `stack=neu` SELBST ausgelöst, weil
  // der freigegebene Nachschub leer war. Bis hierher fiel das Feld auf den Boden — die Antwort
  // trug es, niemand las es. Die Chip-Leiste zeigt daraus denselben Hinweis wie der Wisch-Stapel.
  leadGenFallbackActive: boolean;
  leadGenQualified: number;          // of the filtered set, how many are measured-in-band (status='qualified')
  // Kopfzeile: nur die drei angezeigten Zahlen. Den vollen Satz holt der Einstellungen-Drawer
  // selbst -- er ist der einzige Leser der teuren Felder und laedt erst beim Oeffnen.
  leadGenStats: FunnelStatsHeader | null;
  leadGenLoading: boolean;
  leadGenError: string | null;
  leadGenFilters: LeadGenFilters;
  leadGenSelected: Set<number>;
  leadGenBusy: boolean;
  swScrapeProgress: { done: number; total: number } | null; // live SimilarWeb-scrape progress (SSE-fed)
  swScrapeRunning: boolean;          // a scrape is running server-side (survives page-leave; set on mount)
  enrichProgress: { done: number; total: number } | null; // live Enrichment-Lauf-Fortschritt (SSE-fed)
  enrichRunning: boolean;            // ein Enrichment-Lauf läuft server-seitig
  leadGenScrapePending: number;      // leads the scrape WOULD process (button count, not list size)
  mixScrapeProgress: { done: number; total: number } | null; // live Tier-2 marketing-mix progress
  mixScrapeRunning: boolean;         // a Tier-2 mix scrape is running server-side
  leadGenMixPending: number;         // qualified leads still missing the radeance mix (Tier-2 button count)
  // Fertig-Signale fuer die drei Stufen, die BackgroundTasks nutzen aber (bis auf Toast+Refetch)
  // kein eigenes "laeuft noch"-Flag hatten — Voraussetzung fuer den "Alles starten"-Orchestrator im
  // Funnel-Panel, der reihum auf jede Stufe wartet, bevor er die naechste ansteht. `enrich` hat seit
  // #716 bereits `enrichRunning`/`enrichProgress` — kein zweites Flag dafuer.
  leadGenSourceMetaRunning: boolean;
  leadGenImpressumRunning: boolean;
  leadGenClassifyRunning: boolean;
  leadGenContactsRunning: boolean;
  // Kontaktsuche meldet ihre echten Kosten NUR ueber das SSE-Fertig-Event (`cost_usd`), nicht in
  // der sofortigen POST-Antwort wie Sessions/Mix (`est_cost_usd`) — der "Alles starten"-Orchestrator
  // braucht diese Zahl fuer seine Ist-Kosten-Summe (Spend-Sicherung), darum hier gespeichert.
  leadGenContactsLastCostUsd: number | null;
  leadGenEditingId: number | null;   // lead currently open/edited locally · SSE merges skip it (no clobber)
  scrapePreview: ScrapePreview | null;   // scrape-planner live preview (cost/yield/confidence)
  scrapePreviewLoading: boolean;
  // Funnel-Panel („Leads holen"): alle sechs Stufen mit Rueckstand, Kappe, Kosten, Herkunft, Land.
  funnelPlan: FunnelPlan | null;
  funnelPlanLoading: boolean;
  // Rueckwaerts-Rechner: Ziel "kontaktierbare Leads/Tag" -> noetige Menge + Kosten je Stufe.
  reverseFunnelPlan: ReverseFunnelPlan | null;
  reverseFunnelPlanLoading: boolean;
  icpBand: IcpBand | null;               // active global ICP qualify band (from view-config)
  // Land + E-Commerce vor dem Bezahlen filtern (Nr. 108, docs/entscheidungen.md): EINE Auswahl
  // fuers ganze "Leads holen"-Panel, unabhaengig von der generischen Tabellen-Filterleiste
  // (leadGenFilters.filter) — dieselbe Trennung wie `funnelFilterParams` sie fuer die uebrigen
  // Kriterien schon macht. Pro Nutzer gemerkt (db_web.lead_filter.holen_filter_*).
  holenFilterCountries: string[];
  holenFilterIsEcommerce: boolean | null;
  holenFilterLoaded: boolean;
  countryOptions: { code: string; n: number; label: string }[];
  enrichmentYield: { pending: number; enrich_needed: number | null } | null;
  // Shared-mode live presence: lead_id → setters currently focused on it (SSE-fed, NOT persisted).
  // The card reads this to badge "👤 X schaut gerade". Locks are separate (on the DomainLead row).
  presenceByLead: Record<number, { user_id: number; username: string }[]>;
  applyPresence: (leadId: number, present: { user_id: number; username: string }[]) => void;
  // Klassik Stufe 3: der durable Lock (`leadgen_lock_changed` MIT `lock_until`), live über den
  // Fetch-Snapshot gelegt — sonst hätte Klassik nur den Stand vom letzten `fetchLeadGenLeads()`.
  // Ein `state: 'locked'` OHNE `lock_until` ist reiner Präsenz-Reflex desselben Events (Nr. 12,
  // `_broadcast_presence_change`) und landet bewusst NICHT hier — dafür ist `presenceByLead` da.
  locksByLead: Record<number, { active: boolean; by: number | null; byName: string | null }>;
  applyLock: (leadId: number, lock: { active: boolean; by: number | null; byName: string | null }) => void;

  fetchLeadGenLeads: () => Promise<void>;
  fetchScrapePreview: (params: ScrapePreviewParams) => Promise<void>;
  fetchFunnelPlan: (params: ScrapePreviewParams & { markets?: string | null }) => Promise<void>;
  fetchReverseFunnelPlan: (ziel: number,
                          params: ScrapePreviewParams & { markets?: string | null },
                          modus?: ZielModus) => Promise<void>;
  clearReverseFunnelPlan: () => void;
  saveIcpBand: (min: number, max: number) => Promise<{ qualified: number; disqualified: number } | void>;
  setIcpBand: (band: IcpBand | null) => void;
  setLeadGenEditingId: (id: number | null) => void;
  applyLeadUpdate: (lead: DomainLead) => void;        // live: merge a peer's row update
  applyBulkStatus: (ids: number[], status: string) => void; // live: peer bulk status change
  checkSwScrapeStatus: () => Promise<void>;
  fetchLeadGenStats: () => Promise<void>;
  updateLeadGenStatus: (id: number, status: string) => Promise<void>;
  updateLeadGenLead: (id: number, data: Partial<DomainLead>) => Promise<DomainLead | undefined>;
  bulkLeadGenStatus: (ids: number[], status: string) => Promise<void>;
  bulkLeadGenFindContacts: (
    ids: number[],
    body?: { provider?: 'serper' | 'apify'; limit?: number },
  ) => Promise<BulkFindContactsResult>;
  ingestLeadGen: (replace?: boolean) => Promise<{ inserted: number; updated: number; deleted: number } | void>;
  enrichLeadGen: (limit?: number, filter?: { countries?: string[]; sources?: string[] }) =>
    Promise<{ started: boolean; count: number } | void>;
  enrichImpressumLeadGen: (limit?: number) => Promise<{ started: boolean; count: number; message?: string } | void>;
  classifyLeadGen: (limit?: number) => Promise<{ started: boolean; count: number; message?: string } | void>;
  contactsRunLeadGen: (limit?: number) => Promise<{ started: boolean; count: number; message?: string } | void>;
  // Phase C.2 split: resolve() bankt die Auflösung + klassifiziert (auto vs. Impressum-Rateversuch),
  // execute() bezahlt die eigentliche Personensuche für die genehmigten lead_ids. Beide setzen
  // leadGenContactsRunning/leadGenContactsLastCostUsd selbst (kein SSE nötig) — resolve() setzt
  // running=true zu Beginn, execute() setzt es am Ende wieder false, damit der "Alles starten"-
  // Orchestrator (der auf das Flag wartet) über den ganzen Bestätigungsdialog hinweg korrekt wartet.
  contactsRunResolveLeadGen: (limit?: number) => Promise<{
    auto: { lead_id: number; domain: string | null; target: string | null; source: string | null }[];
    needs_confirmation: { lead_id: number; domain: string | null; target: string | null; source: string | null }[];
    est_cost_usd: number;
  } | void>;
  contactsRunExecuteLeadGen: (leadIds: number[]) => Promise<{
    processed: number; created_total: number; found_total: number; cost_usd: number;
  } | void>;
  markUnresolvableLeadGen: (id: number) => Promise<DomainLead | undefined>;
  setHosApproval: (id: number, approved: boolean) => Promise<DomainLead | undefined>;
  setHosDecision: (id: number, decision: 'approve' | 'reject' | 'reset') => Promise<DomainLead | undefined>;
  sourceMetaLeadGen: () => Promise<{ started: boolean; message?: string } | void>;
  reserveDailyLeadGen: () => Promise<{ date: string; counts: Record<string, number> } | void>;
  /** `targetBatch`: die geplante Lauf-Stueckzahl (naechster_lauf_stueck) — nur fuer das
      ertragsbasierte Auto-Vorziehen gebraucht (Spec Punkt 4), wenn is_ecommerce=true aktiv ist. */
  scrapeSimilarWebLeadGen: (targetBatch?: number) => Promise<{ started: boolean; count: number; est_cost_usd?: number; running?: boolean; message?: string } | void>;
  setSwScrapeProgress: (p: { done: number; total: number } | null) => void;
  setSwScrapeRunning: (running: boolean) => void;
  setEnrichProgress: (p: { done: number; total: number } | null) => void;
  setEnrichRunning: (running: boolean) => void;
  /** `targetBatch`: die geplante Lauf-Stueckzahl (naechster_lauf_stueck) — nur fuer das
      ertragsbasierte Auto-Vorziehen gebraucht (Spec Punkt 4), wenn is_ecommerce=true aktiv ist. */
  scrapeSimilarWebMixLeadGen: (prioritizeAdvertisers?: boolean, targetBatch?: number) => Promise<{ started: boolean; count: number; est_cost_usd?: number; running?: boolean; message?: string } | void>;
  checkMixScrapeStatus: () => Promise<void>;
  setMixScrapeProgress: (p: { done: number; total: number } | null) => void;
  setMixScrapeRunning: (running: boolean) => void;
  setLeadGenFilters: (filters: Partial<LeadGenFilters>) => void;
  setLeadGenSelected: (ids: Set<number>) => void;
  setLeadGenSourceMetaRunning: (running: boolean) => void;
  setLeadGenImpressumRunning: (running: boolean) => void;
  setLeadGenClassifyRunning: (running: boolean) => void;
  setLeadGenContactsRunning: (running: boolean) => void;
  setLeadGenContactsLastCostUsd: (cost: number | null) => void;

  // Land + E-Commerce (Nr. 108) ─────────────────────────────────────────────────────────────
  loadHolenFilter: () => Promise<void>;         // gemerkte Auswahl + Laender-Optionen laden
  setHolenFilterCountries: (countries: string[]) => void;   // setzt lokal + speichert (persistiert)
  setHolenFilterIsEcommerce: (v: boolean | null) => void;
  fetchEnrichmentYield: (target: number) => Promise<void>;
}

// Rank-proxy cutoff for the "≥100k sessions" view (mirrors config.LEADGEN_RANK_PROXY_MAX).
export const RANK_PROXY_MAX = 100000;

// ICP qualification band (business rule): 300k–1M *real* sessions/month (12-mo avg).
// DataForSEO measures organic search only → systematically underestimates real sessions,
// so we scale its raw figure up by DFS_CORRECTION to estimate real sessions, and apply the
// inverse to derive the RAW band used for server-side filtering.
export const ICP_REAL_MIN = 300_000;
export const ICP_REAL_MAX = 1_000_000;
export const DFS_CORRECTION = 1.5; // DataForSEO(organic+paid) × 1.5 ≈ real sessions
export const ICP_RAW_MIN = Math.round(ICP_REAL_MIN / DFS_CORRECTION); // 200_000
export const ICP_RAW_MAX = Math.round(ICP_REAL_MAX / DFS_CORRECTION); // 666_667

// ICP-size Tranco-rank proxy (validated: rank ~15k–50k ≈ 300k–1M real sessions).
// Used to size advertisers that have no DataForSEO traffic data yet.
export const ICP_RANK_MIN = 15_000;
export const ICP_RANK_MAX = 50_000;

// fraud0-customer verticals (AI classification) · the dropdown source of truth.
export const VERTICALS = [
  'E-Commerce & Retail', 'Finance & Insurance', 'Energy & Home',
  'Travel & Hospitality', 'Media & Marketing', 'Other',
] as const;

const DEFAULT_FILTERS: LeadGenFilters = {
  stack: null,               // Klassik Stufe 2: kein Arbeits-Stapel gewählt = die Tabelle wie bisher
  zeilenart: 'firma',        // Klassik ist IMMER eine Firmentabelle, mit oder ohne Stapel-Chip (Stufe 3, E7)
  status: null,
  client_id: null,
  enrichment_status: null,
  search: '',
  max_rank: null,             // obsolete publisher-era rank proxy · off by default
  min_rank: null,             // ICP-size rank window lower bound (advertiser proxy); off
  crux_present: null,         // CrUX-verified filter (off by default until enriched)
  min_sessions: null,         // ICP-band lower bound (raw DataForSEO); off by default
  max_sessions: null,         // ICP-band upper bound (raw DataForSEO); off by default
  site_kind: 'non_publisher', // schließt NULL (noch nicht klassifiziert) ein; nur explizite Publisher raus.
                              // Klassifikation läuft jetzt SPÄT (sessions-gated) → die meisten ICP-Leads
                              // sind vor Haiku site_kind=NULL und müssen sichtbar bleiben.
  business_category: null,    // vertical filter (off by default)
  runs_google_ads: true,      // Google-Ads buying signal (fraud0 ICP); ON by default = qualified ICP list
  has_meta_pixel: null,       // Meta-Pixel paid-media signal; off by default
  has_gtm: null,              // GTM present; off by default
  has_conversion_events: null,// tracks money events (purchase/checkout); off by default
  min_advertiser_score: null, // advertiser-fit threshold; off by default
  max_crux_magnitude: 100000, // CrUX size gate (top-100k DACH); ON by default = qualified ICP list
  min_crux_magnitude: 5000,   // size CEILING (eff-rank ≥5k): drops the CrUX magnitude-1000 bucket,
                              // which prod-calibration (n=207) showed is 93% too-big (>1M visits).
                              // Mirrors nightly LEADGEN_CRUX_MAG_MIN. Do NOT raise to 15k · the whole
                              // funnel sits in the 5k bucket (29% in-band); 15k would empty it.
  min_similarweb_visits: null, // ICP band lower bound: ≥N real SimilarWeb visits; off by default
  max_similarweb_visits: null, // ICP band upper bound: ≤N real SimilarWeb visits; off by default
  exclude_foreign_cctld: true, // drop ilmeteo.it/prom.ua/… (foreign ccTLDs); ON by default
  exclude_subdomains: true,    // drop de.anycubic.com/… (SimilarWeb can't measure subdomains); ON
  exclude_small_market_crux: true, // hide UNMEASURED CrUX leads from small markets (CH/AT/LI); ON by default
  measured: null,              // audit view off by default
  source: null,                // source filter (e.g. 'meta_ads' = daily signal-first trickle); off
  missing_linkedin: null,      // setter work queue (LinkedIn links missing); off by default (on for setter)
  reserved_mine: null,         // "Meine Leads" filter; null=all, true=only my reserved-today leads
  reserved_for: null,          // ADMIN inspect: leads reserved for a specific user today; null=off
  hos_approved: null,          // Head-of-Sales sign-off filter; null=all, true=only approved, false=only open (HoS landing)
  hos_status: null,            // HoS tri-state filter; null=all, 'approved'|'rejected'|'pending'
  marketing_contact_status: null, // 3-stage setter pipeline stage filter; off for the classic table
  setter_rejected: null,       // null=ignore, false=hide rejected, true=only rejected (admin review stack)
  pitch_sent: null,            // Anschreiben-stage gate; off for the classic table
  followup_due: null,          // Nachfassen stack gate; off for the classic table
  min_roi: null,               // computed-ROI floor (setter gate); off by default
  min_paid_share: 0.1,         // write-list gate: ≥ paid-traffic share (0..1); 10% default (matches config.FRAUD0_MIN_PAID_SHARE)
  min_recoverable_eur: null,   // write-list gate: ≥ recoverable ad-fraud €/yr; off by default
  min_search_paid_spend: null, // write-list gate: ≥ monthly search-paid ad spend €; off by default (boss heuristic ~30k)
  recoverable_rate: null,      // live clawback-rate override; null = saved default (from view config)
  prevention_eff: null,        // live prevention-effectiveness override; null = saved default (from view config)
  cpc: null,                   // live search-CPC override €/click; null = saved default (config 2.00, boss dials 1)
  value_channels: ['search_paid'], // paid channels feeding the value; search-only default, display/social opt-in
  best_leads: true,            // "Beste Leads" is the DEFAULT view (write-list); floors/rate seeded from view config on mount
  category_sort: false,        // Kategorie-Priorität nur im Swipe-Stapel (useLeadQueue), nicht in der Tabellenansicht
  exclude_foreign_reserved: false, // nur Swipe-Stapel: Leads anderer User ausblenden (nicht in der Tabellenansicht)
  sort_by: 'value_density',  // write-list default: aufwandsbereinigte Priorisierung (Beste-Leads-Modus)
  sort_dir: 'desc',
  limit: 50,
  offset: 0,
};

// Ertragsbasiertes Auto-Vorziehen (Nr. 108, Spec Punkt 4): bei aktivem is_ecommerce=true-Filter
// vor einer bezahlten Anfrage erst gratis genug nachanreichern, um `targetBatch` zu treffen.
// Gemeinsam fuer scrape_similarweb UND scrape_mix — vorher war das nur in der Mix-Stufe verdrahtet,
// obwohl die Spec ausdruecklich beide Knoepfe verlangt (Review-Fan-out 2026-09-07, bug real=true).
async function autoVorziehenErtrag(
  set: Parameters<StateCreator<LeadGenSlice, [], [], LeadGenSlice>>[0],
  get: Parameters<StateCreator<LeadGenSlice, [], [], LeadGenSlice>>[1],
  countries: string[],
  isEcommerce: boolean | null,
  targetBatch: number | undefined,
): Promise<void> {
  if (isEcommerce !== true || !targetBatch) return;
  const ertrag = await leadGenApi.getEnrichmentYield(countries, targetBatch);
  if (!ertrag.enrich_needed || ertrag.enrich_needed <= 0) return;
  const er = await leadGenApi.enrich(ertrag.enrich_needed, { countries });
  if (!er?.started) return;
  set({ enrichProgress: { done: 0, total: er.count }, enrichRunning: true });
  // Kurze Pause, damit das SSE-Start-Ereignis den Zustand ueberhaupt erreicht (dieselbe Race
  // wie beim "Alles starten"-Orchestrator, Fraud0Leads.tsx::waitForStageDone).
  await new Promise((res) => setTimeout(res, 300));
  const start = Date.now();
  const timeoutMs = 5 * 60 * 1000; // falls ein SSE-Fertig-Event verlorengeht
  while (get().enrichRunning) {
    if (Date.now() - start > timeoutMs) break;
    await new Promise((res) => setTimeout(res, 1500));
  }
}

export const createLeadGenSlice: StateCreator<LeadGenSlice, [], [], LeadGenSlice> = (set, get) => ({
  leadGenLeads: [],
  leadGenTotal: 0,
  leadGenFallbackActive: false,
  leadGenQualified: 0,
  leadGenStats: null,
  leadGenLoading: false,
  leadGenError: null,
  leadGenFilters: DEFAULT_FILTERS,
  leadGenSelected: new Set(),
  leadGenBusy: false,
  swScrapeProgress: null,
  swScrapeRunning: false,
  enrichProgress: null,
  enrichRunning: false,
  leadGenScrapePending: 0,
  mixScrapeProgress: null,
  mixScrapeRunning: false,
  leadGenMixPending: 0,
  leadGenSourceMetaRunning: false,
  leadGenImpressumRunning: false,
  leadGenClassifyRunning: false,
  leadGenContactsRunning: false,
  leadGenContactsLastCostUsd: null,
  leadGenEditingId: null,
  scrapePreview: null,
  scrapePreviewLoading: false,
  funnelPlan: null,
  funnelPlanLoading: false,
  reverseFunnelPlan: null,
  reverseFunnelPlanLoading: false,
  icpBand: null,
  presenceByLead: {},
  locksByLead: {},
  holenFilterCountries: [],
  holenFilterIsEcommerce: null,
  holenFilterLoaded: false,
  countryOptions: [],
  enrichmentYield: null,

  setLeadGenEditingId: (id) => set({ leadGenEditingId: id }),
  setIcpBand: (band) => set({ icpBand: band }),
  // Live presence (shared mode): replace the focus set for one lead; drop the key when empty so the
  // map stays small. Fed by the leadgen_presence_changed SSE event (AppShell).
  applyPresence: (leadId, present) => set((s) => {
    const next = { ...s.presenceByLead };
    if (present && present.length) next[leadId] = present;
    else delete next[leadId];
    return { presenceByLead: next };
  }),
  // Live durable lock (shared mode): mirrors `leadgen_lock_changed` for `state === 'released'` or a
  // `state === 'locked'` event that carries `lock_until` (a real /claim, not the presence reflex of
  // the same event — AppShell filters that before calling this). Drop the key on release so the
  // fallback to the fetch-snapshot (`lead.lock_active`) takes over.
  applyLock: (leadId, lock) => set((s) => {
    const next = { ...s.locksByLead };
    if (lock.active) next[leadId] = lock;
    else delete next[leadId];
    return { locksByLead: next };
  }),

  // Live cost/yield/confidence preview for the scrape planner. Aborts any in-flight request so rapid
  // slider changes never land out of order (Gemini review). Aborted calls resolve silently.
  fetchScrapePreview: async (params) => {
    _previewAbort?.abort();
    const ctrl = new AbortController();
    _previewAbort = ctrl;
    set({ scrapePreviewLoading: true });
    try {
      const res = await leadGenApi.scrapePreview(params, ctrl.signal);
      if (_previewAbort === ctrl) set({ scrapePreview: res, scrapePreviewLoading: false });
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') return; // superseded by a newer call
      if (_previewAbort === ctrl) set({ scrapePreview: null, scrapePreviewLoading: false });
    }
  },

  // Funnel-Panel: alle sechs Stufen in EINER Anfrage. Wird beim OEFFNEN des Panels geladen und
  // nach jedem beendeten Lauf erneut — nicht bei jedem Tastendruck: die Sessions-Stufe zaehlt ueber
  // die gefilterte Menge, das ist die teuerste Abfrage der Seite. Der Server haelt zusaetzlich einen
  // kurzen Zwischenspeicher, damit Auf- und Zuklappen nichts kostet.
  fetchFunnelPlan: async (params) => {
    _funnelAbort?.abort();
    const ctrl = new AbortController();
    _funnelAbort = ctrl;
    set({ funnelPlanLoading: true });
    try {
      const res = await leadGenApi.funnelPlan(params, ctrl.signal);
      if (_funnelAbort === ctrl) set({ funnelPlan: res, funnelPlanLoading: false });
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') return; // von einem neueren Aufruf abgeloest
      if (_funnelAbort === ctrl) set({ funnelPlan: null, funnelPlanLoading: false });
    }
  },

  // Rueckwaerts-Rechner: Ziel eingeben -> noetige Menge/Kosten je Stufe. Eigener Abort-Handle,
  // damit schnelles Tippen im Zielfeld keine Antworten in falscher Reihenfolge zeigt.
  fetchReverseFunnelPlan: async (ziel, params, modus = 'taeglich') => {
    _reverseFunnelAbort?.abort();
    const ctrl = new AbortController();
    _reverseFunnelAbort = ctrl;
    set({ reverseFunnelPlanLoading: true });
    try {
      const res = await leadGenApi.funnelReversePlan(ziel, params, ctrl.signal, modus);
      if (_reverseFunnelAbort === ctrl) set({ reverseFunnelPlan: res, reverseFunnelPlanLoading: false });
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') return;
      if (_reverseFunnelAbort === ctrl) set({ reverseFunnelPlan: null, reverseFunnelPlanLoading: false });
    }
  },
  clearReverseFunnelPlan: () => {
    _reverseFunnelAbort?.abort();
    set({ reverseFunnelPlan: null, reverseFunnelPlanLoading: false });
  },

  // Persist the global ICP band + server re-classifies all measured leads; refresh list + stats so the
  // new qualified pool shows immediately.
  saveIcpBand: async (min, max) => {
    const res = await leadGenApi.saveIcpBand(min, max);
    set({ icpBand: res.band });
    await get().fetchLeadGenLeads();
    await get().fetchLeadGenStats();
    return res.reclassified;
  },

  // Live collaboration: a peer (or this user on another device) changed a lead. Merge the row
  // in place if it's loaded; never touch the lead the local user is actively editing.
  applyLeadUpdate: (lead) => set((s) => {
    if (lead.id === s.leadGenEditingId) return {};
    if (!s.leadGenLeads.some((l) => l.id === lead.id)) return {};
    return { leadGenLeads: s.leadGenLeads.map((l) => (l.id === lead.id ? lead : l)) };
  }),

  applyBulkStatus: (ids, status) => set((s) => {
    const set_ = new Set(ids);
    return {
      leadGenLeads: s.leadGenLeads.map((l) =>
        set_.has(l.id) && l.id !== s.leadGenEditingId ? { ...l, status } : l),
    };
  }),

  fetchLeadGenLeads: async () => {
    set({ leadGenLoading: true, leadGenError: null });
    try {
      // include_pending: only the klassik table renders scrape_pending/mix_pending (the "Sessions holen"
      // / "Marketing-Mix holen" button counts). The V2 swipe view + counter badges never read them and
      // must not pay for the two full-table scans · so the flag lives here, in the klassik store loader.
      const result = await leadGenApi.listLeads({ ...get().leadGenFilters, include_pending: true });
      set({ leadGenLeads: result.items, leadGenTotal: result.total,
            leadGenQualified: result.qualified ?? 0,
            leadGenFallbackActive: result.fallback_active ?? false,
            leadGenScrapePending: result.scrape_pending ?? 0,
            leadGenMixPending: result.mix_pending ?? 0 });
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenLoading: false });
    }
  },

  // On page-mount: a scrape started earlier may still be running server-side (the background task
  // survives leaving/reloading the page). Reflect that so the button shows "läuft…" on return.
  checkSwScrapeStatus: async () => {
    try {
      const r = await leadGenApi.scrapeSimilarWebStatus();
      set({ swScrapeRunning: r.running });
    } catch { /* status is best-effort */ }
  },

  // Die drei Zahlen der Kopfzeile sind eine NEBENAUSKUNFT: fehlen sie, bleibt die Kopfzeile leer,
  // die Tabelle daneben arbeitet weiter. Der Fehler darf deshalb NICHT in `leadGenError` landen —
  // das Fach rendert die Seite als rote Zeile ueber der Liste, und dann sieht ein rollenbedingter
  // 403 einer Randabfrage aus, als waere die ganze Flaeche kaputt. Genau so gemessen am
  // 2026-08-21: `fraud0_setter` bekam 403 auf `/lead-gen/stats` (fehlte als einziger Rolle in der
  // Whitelist), und ueber der voll funktionierenden Tabelle stand „Dieser Account darf nur
  // Fraud0-Leads aufrufen." Die Whitelist ist repariert; diese Zeile sorgt dafuer, dass die
  // NAECHSTE Randabfrage denselben Eindruck nicht wieder erzeugt.
  fetchLeadGenStats: async () => {
    try {
      const stats = await leadGenApi.getStatsHeader(get().leadGenFilters.client_id);
      set({ leadGenStats: stats });
    } catch { /* Kopfzahlen sind best-effort — siehe oben */ }
  },

  updateLeadGenStatus: async (id, status) => {
    const updated = await leadGenApi.updateLead(id, { status });
    set((s) => ({ leadGenLeads: s.leadGenLeads.map((l) => (l.id === id ? updated : l)) }));
    get().fetchLeadGenStats();
  },

  updateLeadGenLead: async (id, data) => {
    const updated = await leadGenApi.updateLead(id, data);
    set((s) => ({ leadGenLeads: s.leadGenLeads.map((l) => (l.id === id ? updated : l)) }));
    return updated;
  },

  markUnresolvableLeadGen: async (id) => {
    const updated = await leadGenApi.markUnresolvable(id);
    set((s) => ({ leadGenLeads: s.leadGenLeads.map((l) => (l.id === id ? updated : l)) }));
    return updated;
  },

  setHosApproval: async (id, approved) => {
    const updated = await leadGenApi.hosApprove(id, approved);
    set((s) => ({ leadGenLeads: s.leadGenLeads.map((l) => (l.id === id ? updated : l)) }));
    return updated;
  },

  setHosDecision: async (id, decision) => {
    const updated = await leadGenApi.hosDecision(id, decision);
    set((s) => ({ leadGenLeads: s.leadGenLeads.map((l) => (l.id === id ? updated : l)) }));
    return updated;
  },

  bulkLeadGenStatus: async (ids, status) => {
    await leadGenApi.bulkStatus(ids, status);
    await get().fetchLeadGenLeads();
    get().fetchLeadGenStats();
    set({ leadGenSelected: new Set() });
  },

  // Paid people-search over a set of leads. Selection is NOT cleared here · the page drives the
  // canary flow (run a slice, checkpoint, run the rest) and clears the selection when done.
  bulkLeadGenFindContacts: async (ids, body = {}) => {
    const res = await leadGenApi.bulkFindContacts(ids, { provider: 'apify', ...body });
    await get().fetchLeadGenLeads();
    get().fetchLeadGenStats();
    return res;
  },

  ingestLeadGen: async (replace = true) => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      const result = await leadGenApi.ingest(replace);
      await get().fetchLeadGenLeads();
      get().fetchLeadGenStats();
      return result;
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  enrichLeadGen: async (limit, filter) => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      // `enrichRunning` kippt ueber die SSE-Kette (leadgen_enrich_start, siehe AppShell), nicht
      // synchron hier — der Background-Task broadcastet ihn selbst, sobald er anlaeuft.
      // Eine manuelle Kachel-Auswahl (`filter`, ueber onClickMitFilter) ist spezifischer und
      // gewinnt; ohne sie greift das Panel-Land als Default (Nr. 108-Ausweitung), statt komplett
      // ungefiltert zu laufen.
      const holenCountries = get().holenFilterCountries;
      const effektiv = filter ?? (holenCountries.length ? { countries: holenCountries } : undefined);
      return await leadGenApi.enrich(limit, effektiv);
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  enrichImpressumLeadGen: async (limit) => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      // Land + E-Commerce (Panel) — is_ecommerce ist hier bereits bekannt (Stufe laeuft nach
      // Classify), gilt also als echter Hard-Filter wie bei Sessions/Mix.
      const holen = buildHolenFilterJson(get().holenFilterCountries, get().holenFilterIsEcommerce);
      const r = await leadGenApi.enrichImpressum(limit, holen ? { v: 1, alle: holen.alle } : undefined);
      if (r?.started) set({ leadGenImpressumRunning: true });
      return r;
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  classifyLeadGen: async (limit) => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      // Nur der Land-Teil wirkt hier (is_ecommerce ist vor Classify noch unbekannt) — der Server
      // ignoriert eine mitgeschickte is_ecommerce-Bedingung an dieser Stelle bewusst.
      const holen = buildHolenFilterJson(get().holenFilterCountries, null);
      const r = await leadGenApi.classify(limit, holen ? { v: 1, alle: holen.alle } : undefined);
      if (r?.started) set({ leadGenClassifyRunning: true });
      return r;
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  contactsRunLeadGen: async (limit) => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      const r = await leadGenApi.contactsRun(limit);
      if (r?.started) set({ leadGenContactsRunning: true });
      return r;
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  contactsRunResolveLeadGen: async (limit) => {
    set({ leadGenBusy: true, leadGenError: null, leadGenContactsRunning: true });
    try {
      // Land + E-Commerce (Panel) — is_ecommerce ist hier bereits bekannt, gilt als Hard-Filter.
      const holen = buildHolenFilterJson(get().holenFilterCountries, get().holenFilterIsEcommerce);
      return await leadGenApi.contactsRunResolve(limit, holen ? { v: 1, alle: holen.alle } : undefined);
    } catch (e) {
      set({ leadGenError: (e as Error).message, leadGenContactsRunning: false });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  contactsRunExecuteLeadGen: async (leadIds) => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      const r = await leadGenApi.contactsRunExecute(leadIds);
      set({ leadGenContactsLastCostUsd: r.cost_usd });
      await get().fetchLeadGenLeads();
      return r;
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false, leadGenContactsRunning: false });
    }
  },

  sourceMetaLeadGen: async () => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      const r = await leadGenApi.sourceMeta();
      if (r?.started) set({ leadGenSourceMetaRunning: true });
      return r;
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  // Admin: trigger the daily reservation. The server broadcasts leadgen_reservations_updated, so the
  // live refetch happens in the AppShell SSE handler (covers every client, not just this one).
  reserveDailyLeadGen: async () => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      return await leadGenApi.reserveDaily();
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  scrapeSimilarWebLeadGen: async (targetBatch) => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      const f = get().leadGenFilters;
      const countries = get().holenFilterCountries;
      const isEcommerce = get().holenFilterIsEcommerce;
      // Land/E-Commerce (Nr. 108) UND die bestehende Tabellen-Filterleiste sind zwei unabhaengige
      // AND-Bedingungslisten auf demselben lead_filter-Format — beide gemeinsam an "filter" haengen,
      // statt eine die andere ueberschreiben zu lassen.
      const holen = buildHolenFilterJson(countries, isEcommerce);
      const alle: FilterBedingung[] = [...(f.filter?.alle ?? []), ...(holen?.alle ?? [])];

      await autoVorziehenErtrag(set, get, countries, isEcommerce, targetBatch);

      const r = await leadGenApi.scrapeSimilarWeb({
        ...f, ...(alle.length ? { filter: { v: 1, alle } as LeadFilter } : {}),
      });
      // Show the progress bar immediately; SSE leadgen_similarweb_progress refines done/total,
      // leadgen_similarweb_complete (handled in AppShell) clears it + refetches.
      if (r?.started) set({ swScrapeProgress: { done: 0, total: r.count }, swScrapeRunning: true });
      return r;
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },

  setSwScrapeProgress: (p) => set({ swScrapeProgress: p }),
  setSwScrapeRunning: (running) => set({ swScrapeRunning: running }),
  setEnrichProgress: (p) => set({ enrichProgress: p }),
  setEnrichRunning: (running) => set({ enrichRunning: running }),

  scrapeSimilarWebMixLeadGen: async (prioritizeAdvertisers = true, targetBatch) => {
    set({ leadGenBusy: true, leadGenError: null });
    try {
      const f = get().leadGenFilters;
      const countries = get().holenFilterCountries;
      const isEcommerce = get().holenFilterIsEcommerce;
      const holen = buildHolenFilterJson(countries, isEcommerce);
      const alle: FilterBedingung[] = [...(f.filter?.alle ?? []), ...(holen?.alle ?? [])];
      const filter: LeadFilter | undefined = alle.length ? { v: 1, alle } : undefined;

      await autoVorziehenErtrag(set, get, countries, isEcommerce, targetBatch);

      const r = await leadGenApi.scrapeSimilarWebMix(prioritizeAdvertisers, filter);
      if (r?.started) set({ mixScrapeProgress: { done: 0, total: r.count }, mixScrapeRunning: true });
      return r;
    } catch (e) {
      set({ leadGenError: (e as Error).message });
    } finally {
      set({ leadGenBusy: false });
    }
  },
  checkMixScrapeStatus: async () => {
    try {
      const r = await leadGenApi.scrapeSimilarWebMixStatus();
      set({ mixScrapeRunning: r.running });
    } catch { /* status is best-effort */ }
  },
  setMixScrapeProgress: (p) => set({ mixScrapeProgress: p }),
  setMixScrapeRunning: (running) => set({ mixScrapeRunning: running }),

  setLeadGenFilters: (filters) => {
    set((s) => ({
      leadGenFilters: { ...s.leadGenFilters, ...filters, offset: filters.offset ?? 0 },
      leadGenSelected: new Set(),
    }));
    get().fetchLeadGenLeads();
  },

  setLeadGenSelected: (ids) => set({ leadGenSelected: ids }),

  setLeadGenSourceMetaRunning: (running) => set({ leadGenSourceMetaRunning: running }),
  setLeadGenImpressumRunning: (running) => set({ leadGenImpressumRunning: running }),
  setLeadGenClassifyRunning: (running) => set({ leadGenClassifyRunning: running }),
  setLeadGenContactsRunning: (running) => set({ leadGenContactsRunning: running }),
  setLeadGenContactsLastCostUsd: (cost) => set({ leadGenContactsLastCostUsd: cost }),

  // Land + E-Commerce (Nr. 108) ─────────────────────────────────────────────────────────────
  loadHolenFilter: async () => {
    try {
      const [gespeichert, optionen] = await Promise.all([
        leadGenApi.getHolenFilter(),
        leadGenApi.getCountryOptions(),
      ]);
      set({
        holenFilterCountries: gespeichert.countries,
        holenFilterIsEcommerce: gespeichert.is_ecommerce,
        countryOptions: optionen.countries,
        holenFilterLoaded: true,
      });
    } catch {
      // Best-effort: ohne gemerkte Auswahl startet das Panel einfach ungefiltert.
      set({ holenFilterLoaded: true });
    }
  },
  setHolenFilterCountries: (countries) => {
    set({ holenFilterCountries: countries });
    leadGenApi.setHolenFilter({ countries, is_ecommerce: get().holenFilterIsEcommerce }).catch(() => {});
  },
  setHolenFilterIsEcommerce: (v) => {
    set({ holenFilterIsEcommerce: v });
    leadGenApi.setHolenFilter({ countries: get().holenFilterCountries, is_ecommerce: v }).catch(() => {});
  },
  fetchEnrichmentYield: async (target) => {
    try {
      const y = await leadGenApi.getEnrichmentYield(get().holenFilterCountries, target);
      set({ enrichmentYield: { pending: y.pending, enrich_needed: y.enrich_needed } });
    } catch {
      set({ enrichmentYield: null });
    }
  },
});

// Baut die Land/E-Commerce-Bedingungen aus dem gemerkten "Leads holen"-Zustand als
// db_web.lead_filter-Payload — oder `undefined`, wenn nichts aktiv ist (kein leeres `{alle:[]}`
// mitschicken, das waere nicht dasselbe wie "kein Filter" fuer den Cache-Signatur-Vergleich in
// funnel_plan()). Modulweit exportiert, weil sowohl die Slice-Aktionen unten als auch
// Fraud0Leads.tsx (funnelFilterParams) sie brauchen.
export function buildHolenFilterJson(
  countries: string[], isEcommerce: boolean | null,
): { alle: FilterBedingung[] } | undefined {
  const bedingungen: FilterBedingung[] = [];
  if (countries.length) bedingungen.push({ k: 'country', op: 'in', w: countries });
  if (isEcommerce != null) bedingungen.push({ k: 'is_ecommerce', op: 'eq', w: isEcommerce });
  return bedingungen.length ? { alle: bedingungen } : undefined;
}
